import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const inputVariants = cva(
  "flex w-full border font-medium transition-all duration-200 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "rounded-lg border-gray-300 bg-white text-gray-900 focus-visible:ring-primary-500 focus-visible:border-primary-500 dark:border-dark-300 dark:bg-dark-200 dark:text-dark-900 dark:focus-visible:ring-accent-500 dark:focus-visible:border-accent-500",
        success: "rounded-lg border-success bg-white text-gray-900 focus-visible:ring-success focus-visible:border-success dark:bg-dark-200 dark:text-dark-900",
        error: "rounded-lg border-danger bg-white text-gray-900 focus-visible:ring-danger focus-visible:border-danger dark:bg-dark-200 dark:text-dark-900",
        "dark-accent": "rounded-lg border-gray-300 bg-gray-50 text-gray-900 focus-visible:ring-primary-500 focus-visible:border-primary-500 dark:border-accent-500 dark:bg-dark-200 dark:text-dark-900 dark:focus-visible:ring-accent-500 dark:focus-visible:border-accent-500 dark:placeholder:text-dark-600",
        "login": "rounded-xl border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent px-4 py-3",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        default: "h-10 px-3 text-base", 
        lg: "h-12 px-4 text-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export const Input = React.forwardRef(({
  className,
  type = "text",
  variant,
  size,
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  required = false,
  ...props
}, ref) => {
  const inputId = props.id || `input-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 dark:text-dark-700 mb-1"
        >
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 dark:text-dark-600">
            {leftIcon}
          </div>
        )}
        
        <input
          id={inputId}
          type={type}
          className={cn(
            inputVariants({ variant: error ? "error" : variant, size }),
            leftIcon && "pl-10",
            rightIcon && "pr-10",
            className
          )}
          ref={ref}
          {...props}
        />
        
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-500 dark:text-dark-600">
            {rightIcon}
          </div>
        )}
      </div>
      
      {(error || helperText) && (
        <div className="mt-1">
          {error && (
            <p className="text-sm text-danger">{error}</p>
          )}
          {helperText && !error && (
            <p className="text-sm text-gray-500 dark:text-dark-600">{helperText}</p>
          )}
        </div>
      )}
    </div>
  );
});

Input.displayName = "Input";

// Specialized search input component
export const SearchInput = React.forwardRef(({
  className,
  placeholder = "Search...",
  leftIcon = <i className="fa-solid fa-search" />,
  size = "default",
  variant = "default",
  ...props
}, ref) => {
  return (
    <Input
      ref={ref}
      type="search"
      placeholder={placeholder}
      leftIcon={leftIcon}
      variant={variant}
      size={size}
      className={className}
      {...props}
    />
  );
});

SearchInput.displayName = "SearchInput";

// Password input with show/hide toggle
export const PasswordInput = React.forwardRef(({
  className,
  showPasswordToggle = true,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const togglePassword = () => setShowPassword(!showPassword);

  return (
    <Input
      ref={ref}
      type={showPassword ? "text" : "password"}
      rightIcon={
        showPasswordToggle ? (
          <button
            type="button"
            onClick={togglePassword}
            className="text-gray-400 hover:text-gray-600 dark:text-dark-400 dark:hover:text-dark-600 transition-colors pointer-events-auto"
          >
            <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
          </button>
        ) : undefined
      }
      className={className}
      {...props}
    />
  );
});

PasswordInput.displayName = "PasswordInput";

// Checkbox component
const checkboxVariants = cva(
  "rounded border-gray-300 text-primary-600 focus:ring-primary-500 transition-colors",
  {
    variants: {
      size: {
        sm: "h-4 w-4",
        default: "h-5 w-5",
        lg: "h-6 w-6",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

export const Checkbox = React.forwardRef(({
  className,
  size,
  label,
  helperText,
  error,
  id,
  ...props
}, ref) => {
  const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="flex items-start">
      <div className="flex items-center h-5">
        <input
          id={checkboxId}
          type="checkbox"
          className={cn(checkboxVariants({ size }), error && "border-red-500 text-red-600 focus:ring-red-500", className)}
          ref={ref}
          {...props}
        />
      </div>
      {label && (
        <div className="ml-3 text-sm">
          <label htmlFor={checkboxId} className="font-medium text-gray-900 dark:text-dark-900">
            {label}
          </label>
          {helperText && (
            <p className="text-gray-500 dark:text-dark-500">{helperText}</p>
          )}
          {error && (
            <p className="text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>
      )}
    </div>
  );
});

Checkbox.displayName = "Checkbox"; 